<template>
  <div
    v-if="pairedProducts.length > 0"
    class="complete-the-set"
    :class="classes"
    data-testid="complete-the-set"
  >
    <div class="container">
      <div class="heading">
        <h2 class="title">How To Style It</h2>
        <p class="intro">Shop The Full Look</p>
      </div>

      <div class="layout">
        <div class="parent-media">
          <Media
            v-if="showParentStyleMedia"
            :sources="product.howToStyleItMedia.reference.sources"
            :images="product.howToStyleItMedia.reference.image"
          />
          <picture v-else-if="parentFlatlayImage?.smallMedia">
            <img
              :src="parentFlatlayImage.smallMedia"
              :alt="product.title"
              class="parent-media__image"
            />
          </picture>
        </div>

        <div class="summary">
          <div
            v-if="mq.lgPlus"
            class="product-list"
            :class="productListClasses"
          >
            <ProductCardHelper
              v-for="(styleProduct, index) in pairedProducts"
              :key="styleProduct.id"
              v-slot="{ primaryMedia }"
              :product="styleProduct"
              :use-campaign-first-override="false"
              image-type="flatlayImages"
            >
              <ProductCard
                :product="styleProduct"
                :media="primaryMedia"
                product-card-variant="journal"
                :class="`product product-${index}`"
              />
            </ProductCardHelper>
          </div>

          <Carousel
            v-else
            class="product-carousel"
            width="200px"
            width-desktop="200px"
            inline
            no-controls
          >
            <div
              v-for="(styleProduct, index) in pairedProducts"
              :key="styleProduct.id"
              class="product-carousel__slide"
            >
              <ProductCardHelper
                v-slot="{ primaryMedia }"
                :product="styleProduct"
                :use-campaign-first-override="false"
                image-type="flatlayImages"
              >
                <ProductCard
                  :product="styleProduct"
                  :media="primaryMedia"
                  product-card-variant="journal"
                  :class="`product product-${index}`"
                />
              </ProductCardHelper>
            </div>
          </Carousel>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import productIdQuery from "@/graphql/queries/ProductId.gql";
import { mapState } from "vuex";
import { unwrapNodes } from "@/helpers/graphql";
import {
  getStyleWithPairingsFromMetafield,
  toProductGid,
} from "@/helpers/styleWithPairings.js";
import Media from "@/components/Media.vue";
import Carousel from "@/components/Carousel.vue";
import ProductCard from "@/components/ProductCard.vue";
import ProductCardHelper from "@/components/product-card/ProductCardHelper.vue";

export default {
  name: "CompleteTheSet",
  components: {
    Media,
    Carousel,
    ProductCard,
    ProductCardHelper,
  },
  inject: ["mq"],
  data() {
    return {
      products: [],
    };
  },
  computed: {
    ...mapState("product", ["product"]),
    orderedProducts() {
      return [...this.products].sort((a, b) => a.order - b.order);
    },
    pairedProducts() {
      return this.orderedProducts.filter(
        (item) => item.id && item.id !== this.product?.id
      );
    },
    classes() {
      return {
        "complete-the-set--few-items": this.pairedProducts.length < 3,
      };
    },
    productListClasses() {
      return {
        "product-list--grid": this.pairedProducts.length >= 3,
        "product-list--column": this.pairedProducts.length < 3,
      };
    },
    showParentStyleMedia() {
      return !!this.product?.howToStyleItMedia?.reference;
    },
    parentFlatlayImage() {
      const flatlayImage = unwrapNodes(
        this.product?.flatlayImages?.references?.edges
      )?.[0]?.image;

      if (flatlayImage?.smallMedia) {
        return flatlayImage;
      }

      const groupedImage = this.product?.groupedImage?.reference?.image;

      if (groupedImage?.smallMedia) {
        return groupedImage;
      }

      return unwrapNodes(this.product?.images?.edges)?.[0] || null;
    },
  },
  watch: {
    product: {
      immediate: true,
      handler() {
        this.loadGroupedProducts();
      },
    },
  },
  methods: {
    async loadGroupedProducts() {
      if (!this.product) {
        return;
      }

      try {
        const pairings = getStyleWithPairingsFromMetafield(this.product, {
          currentProductId: this.product?.id,
        });

        if (!pairings.length) {
          this.products = [];
          return;
        }

        await this.resolvePairedProducts(pairings);
      } catch (error) {
        console.error("CompleteTheSet: failed to load style pairings", error);
        this.products = [];
      }
    },
    async resolvePairedProducts(pairings) {
      this.products = [];

      const productsToFetch = [];

      pairings.forEach((item) => {
        if (item.product) {
          this.products.push({ ...item.product, order: item.order });
          return;
        }

        const productGid = toProductGid(item.id);

        if (productGid === this.product?.id) {
          this.products.push({ ...this.product, order: item.order });
          return;
        }

        productsToFetch.push(item);
      });

      if (!productsToFetch.length) {
        return;
      }

      const promises = productsToFetch.map(({ id }) =>
        this.$apollo.query({
          query: productIdQuery,
          variables: {
            productId: toProductGid(id),
          },
          fetchPolicy: "no-cache",
        })
      );

      const results = await Promise.allSettled(promises);

      results.forEach((result, index) => {
        if (result.status !== "fulfilled") {
          return;
        }

        const product = result.value?.data?.product;

        if (!product) {
          return;
        }

        const match = productsToFetch[index];

        if (!match) {
          return;
        }

        this.products.push({
          ...product,
          order: match.order,
        });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.complete-the-set {
  background-color: $color-grey-lightest;
  padding: 40px 0 60px;

  @include respond-above(lg) {
    padding: 60px 0;
  }

  .container {
    max-width: 448px;

    @include respond-above(lg) {
      max-width: 780px;
    }

    @include respond-above(xl) {
      max-width: 980px;
    }
  }
}

.heading {
  text-align: center;
  margin-bottom: 40px;

  @include respond-above(lg) {
    margin-bottom: 60px;
  }

  .title {
    @include editorial-headline(24px);
    text-transform: uppercase;
    margin-bottom: 16px;

    @include respond-above(lg) {
      @include editorial-headline(28px);
    }
  }

  .intro {
    @include editorial-body(14px);
    font-style: italic;
  }
}

.layout {
  @include respond-above(lg) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 46px;
    align-items: start;
  }
}

.complete-the-set--few-items {
  .layout {
    @include respond-above(lg) {
      grid-template-columns: 457fr 213fr;
      gap: 30px;
    }

    @include respond-above(xl) {
      gap: 80px;
      grid-template-columns: 524fr 290fr;
    }
  }
}

.parent-media {
  margin: 0 auto 20px;

  @include respond-above(lg) {
    margin-bottom: 0;
    position: sticky;
    top: 130px;
    z-index: 2;
    align-self: start;
  }
}

.parent-media__image {
  display: block;
  width: 100%;
  height: auto;
  object-fit: contain;
}

.summary {
  @include respond-above(lg) {
    align-self: start;
  }
}

.product-list {
  display: grid;
  gap: 20px;
}

.product-list--column {
  grid-template-columns: 1fr;
  max-width: 200px;
  margin: 0 auto;

  @include respond-above(lg) {
    max-width: none;
    margin: 0;
  }
}

.product-list--grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  max-width: 420px;
  margin: 0 auto;

  @include respond-above(lg) {
    max-width: none;
    margin: 0;
  }
}

.product-carousel {
  :deep(.carousel) {
    padding-left: 0;
    gap: 20px;
  }

  :deep(.carousel > *) {
    scroll-margin-left: 0;
  }
}

.product-carousel__slide {
  flex: 0 0 200px;
  width: 200px;
}
</style>

<style>
.complete-the-set,
.complete-the-set .container,
.complete-the-set .layout {
  overflow: visible !important;
  height: auto !important;
}
</style>
